/* =============================================================================
LiveAgent365Header_v03.tsx

Version: v02
Date: 2026-02-21

Description:
- Global header component for LiveAgent365 pages.
- Split-only refactor: removes hidden dependencies by accepting nav items + section ids via props.
- Backwards compatible: brand/phone props are still required (as before).
================================================================================ */

import React from "react";
import { Container, Nav, Navbar, Button } from "react-bootstrap";
import { Headset, Telephone } from "react-bootstrap-icons";

export type LiveAgent365NavItem = {
  id: string;
  label: React.ReactNode;
};

export type LiveAgent365HeaderProps = {
  brandName: string;
  phoneDisplay: string;
  phoneTel: string;

  /** Optional: provide nav items to show links in the header (Home page) */
  navItems?: LiveAgent365NavItem[];

  /** Optional: callback to scroll/navigate to a section id */
  onNavToSection?: (id: string) => void;

  /** Optional: which section id should the "Request a demo" button navigate to */
  contactSectionId?: string;
};

export const LiveAgent365Header: React.FC<LiveAgent365HeaderProps> = ({
  brandName,
  phoneDisplay,
  phoneTel,
  navItems = [],
  onNavToSection,
  contactSectionId = "contact",
}) => {
  return (
    <Navbar
      expand="lg"
      className="py-3 border-bottom"
      bg="white"
      sticky="top"
      data-sticky-nav="true"
      style={{
        boxShadow: "0 10px 28px rgba(0,0,0,.08)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Container>
        <Navbar.Brand className="fw-bold" style={{ cursor: "pointer" }} href="/">
          <div className="d-flex align-items-start">
            <div className="d-flex flex-column" style={{ width: 36, minWidth: 36 }} aria-hidden>
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-4"
                style={{ width: 36, height: 36, background: "rgba(13,110,253,.14)" }}
              >
                <Headset />
              </span>
              <div className="d-lg-none mt-2" />
            </div>

            <div className="ms-3">
              <div className="fw-bold" style={{ letterSpacing: ".2px" }}>
                {brandName}
              </div>
              <div className="small text-muted">AI Automation & Reputation Managment</div>
            </div>
          </div>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="liveagent365-nav" />

        <Navbar.Collapse id="liveagent365-nav">
          <Nav className="ms-auto align-items-lg-center gap-lg-2">
            {navItems.map((item) => (
              <Nav.Link
                key={item.id}
                onClick={(e) => {
                  if (!onNavToSection) return;
                  e.preventDefault();
                  onNavToSection(item.id);
                }}
                href={`#${item.id}`}
              >
                {item.label}
              </Nav.Link>
            ))}

            <div className="d-flex align-items-center gap-2 ms-lg-2 mt-3 mt-lg-0">
              <a
                href={`tel:${phoneTel}`}
                className="text-decoration-none d-inline-flex align-items-center gap-2"
                aria-label={`Call ${phoneDisplay}`}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-4"
                  style={{ width: 34, height: 34, background: "rgba(25,135,84,.12)" }}
                  aria-hidden
                >
                  <Telephone />
                </span>
                <span className="fw-semibold">{phoneDisplay}</span>
              </a>

              <Button
                variant="primary"
                className="rounded-pill px-3"
                onClick={() => onNavToSection?.(contactSectionId)}
              >
                Request a demo
              </Button>
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};
